import React from 'react';
import useReveal from '../../AIEmployees/useReveal';
import { trackEvent } from '../../../utils/analytics';
import { JsonLd } from '../../../utils/structuredData';

/**
 * A2AFAQ — objection-handling + AEO surface for /agent-to-agent.
 *
 * Each answer is written answer-first (the first sentence stands alone) so AI
 * answer engines can lift it verbatim, and the whole set ships as FAQPage
 * structured data. Questions carry the category keywords this page targets:
 * "API for AI agents", "OpenClaw skills", "Hermes", coding-agent plugins.
 */
const FAQS = [
  {
    q: 'What is the CellCog agent-to-agent API?',
    a: 'The CellCog agent-to-agent API lets any AI agent use every CellCog capability through one API key: deep research, documents, PDFs, video, dashboards, and apps. Your agent delegates a task and receives finished work back, the same output a human CellCog user would get.',
  },
  {
    q: 'How do OpenClaw agents use CellCog?',
    a: 'CellCog publishes skills for OpenClaw agents. Install a CellCog skill, add your CellCog API key, and your OpenClaw agent can delegate research, document, and media tasks to CellCog and receive the finished files. The Python SDK covers anything beyond the packaged skills.',
  },
  {
    q: 'Does CellCog work with Hermes agents?',
    a: 'Yes. Hermes agents call CellCog through the CellCog Python SDK: one API key exposes the full platform, and results are returned to your agent when the work completes.',
  },
  {
    q: 'Which coding agents can use CellCog?',
    a: 'Claude Code, Cursor, and Codex can all call CellCog through the CellCog plugin and the Open Plugins standard. Your coding agent can generate deep research, documents, and media without leaving the IDE. Any Open Plugins–conformant agent can integrate the same way. Or connect over MCP, no key needed.',
  },
  {
    // Oct 10 2026 (Nitish): the MCP connector as the second way in, see A2AHowItWorks.
    q: 'Does CellCog work over MCP?',
    a: 'Yes. Add https://cellcog.ai/mcp as a remote MCP server in Claude, Cursor, Codex or any client that speaks MCP, sign in with your CellCog account and approve. No API key is needed, and your assistant gets finished work back as links.',
  },
  {
    q: 'How much does the CellCog API cost?',
    a: 'Try it free, no credit card needed. Every account starts with 200 credits, and an API key works on the free tier. After that, billing is usage-based: you pay for the work your agents delegate, with plans from $8/month.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const A2AFAQ = () => {
  const [ref, visible] = useReveal();
  const seenRef = React.useRef(false);
  React.useEffect(() => {
    if (visible && !seenRef.current) {
      seenRef.current = true;
      trackEvent('section_view', { section: 'faq', page: 'agent-to-agent' });
    }
  }, [visible]);
  return (
    <section className="a2a-faq aie-section" id="a2a-faq" ref={ref}>
      <JsonLd data={faqSchema} />
      <div className="aie-section-head">
        <h2 className="aie-section-title aie-display">Questions agents&rsquo; builders ask</h2>
      </div>
      <div className={`a2a-faq__list aie-reveal ${visible ? 'is-visible' : ''}`}>
        {FAQS.map((f) => (
          <div className="a2a-faq__item" key={f.q}>
            <h3 className="a2a-faq__q">{f.q}</h3>
            <p className="a2a-faq__a">{f.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default A2AFAQ;
